import { useEffect, useMemo, useState } from 'react'
import useSocketEvent from './useSocketEvent'
import { useAppSelector } from 'hooks/reduxHooks'
import { selectLoggedIn } from 'selectors/accountSelectors'
import useChatWelcomeMessage from './useChatWelcomeMessage'
import useChatMessages from './useChatMessages'
import { useLocation } from 'react-router'

function useChatState(): [Array<App.ChatMessage>, boolean, boolean, boolean | undefined] {
  const notLoggedIn = !useAppSelector(selectLoggedIn)

  const currentChatId = useAppSelector(state => state.supportAssistant.currentChatId)
  const chatsFetching = useAppSelector(state => state.supportAssistant.chats.fetching)

  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const listOrders = queryParams.get('list-orders') === 'true'

  const [welcomeMessage, welcomeMessageFetching] = useChatWelcomeMessage()
  const [messages, messagesLoading] = useChatMessages(currentChatId ?? 'default')

  const [typing, setTyping] = useState<boolean>(false)
  const [agentTyping, setAgentTyping] = useState<boolean>(false)

  useSocketEvent('response:typing', () => setTyping(true))
  useSocketEvent('response:agent-typing', () => setAgentTyping(true))

  const realMessages = useMemo(() => {
    if (!messages || !Array.isArray(messages)) return []

    if (notLoggedIn && welcomeMessage) {
      if (messages.length === 0) return [welcomeMessage]

      // If the only message is the welcome message, do not inject it into the messages.
      if (messages.length === 1 && messages[0].sender !== 'chatbot') {
        return [welcomeMessage, ...messages]
      }
    }

    return messages.length === 1 && welcomeMessage && messages[0].sender !== 'chatbot' ? [welcomeMessage, ...messages] : [...messages]
  }, [messages, notLoggedIn, welcomeMessage])

  const fetching = welcomeMessageFetching || messagesLoading || realMessages.length === 0 || listOrders && chatsFetching

  useEffect(() => {
    if (realMessages.length > 1) {
      const lastMessage = realMessages[realMessages.length - 1]
      if (lastMessage.sender !== 'user') {
        setTyping(false)
        setAgentTyping(false)
      }
    }
  }, [realMessages])

  return [
    realMessages,
    typing,
    agentTyping,
    fetching,
  ]
}

export default useChatState
